// stores/recommendationStore.ts
import type { ComputedRef, Ref } from 'vue'
import type { DetailTabKey } from '~/types/Tracking'
import type { ProgramRecommendationObj, ProfessionRecommendationObj } from '~/types/school'
import type { JobRecommendationObj } from '~/types/JobRecommendation'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useFrontApi } from '~/composables/useFrontApi'
import { toProgramInteractionPayload, useTracking } from '~/composables/useTracking'
import axios from 'axios'
import { TRACKING_EVENTS } from '~/constants/trackingEvents'

type StoreStatus = 'idle' | 'loading' | 'ready' | 'error'

type RecommendationsDto = {
  id: string | null
  programs: ProgramRecommendationObj[] | null
  professions: ProfessionRecommendationObj[] | null
  jobs: JobRecommendationObj[] | null
}

export const useRecommendationStore = defineStore('recommendation', () => {
  const id = ref<string>('')
  const programs = ref<ProgramRecommendationObj[]>([])
  const professions = ref<ProfessionRecommendationObj[]>([])
  const jobs: Ref<JobRecommendationObj[]> = ref([])
  const status = ref<StoreStatus>('idle')

  let controller: AbortController | null = null

  const programsCount = computed(() => programs.value.length)
  const professionsCount = computed(() => professions.value.length)
  const jobsCount: ComputedRef<number> = computed((): number => jobs.value.length)
  const hasRecommendations = computed(
    () => programs.value.length > 0 || professions.value.length > 0 || jobs.value.length > 0,
  )

  const { track } = useTracking()

  async function fetch(): Promise<void> {
    controller?.abort()
    controller = new AbortController()
    status.value = 'loading'

    try {
      const { data } = await useFrontApi().get<RecommendationsDto>('/api/Recommendations', {
        signal: controller.signal,
      })

      id.value = data.id ?? ''
      programs.value = data.programs ?? []
      professions.value = data.professions ?? []
      jobs.value = data.jobs ?? []

      status.value = 'ready'
    } catch (e) {
      if (axios.isCancel(e) || (e as Error).name === 'AbortError') return
      status.value = 'error'
      throw e
    }
  }

  /**
   * POST vers une action programme recommandé, puis trace l'event PostHog associé.
   * @param {number} programId - Identifiant du programme.
   * @param {string} action - Segment d'URL après `/programs/{id}/`.
   * @param {string} message - Message envoyé au back (vide pour les actions sans message).
   * @param {() => void} onSuccess - Callback de tracking après succès API.
   * @returns {Promise<void>}
   */
  async function postProgramAction(
    programId: number,
    action: string,
    message: string,
    onSuccess: () => void,
  ): Promise<void> {
    try {
      await useFrontApi().post(`/api/Recommendations/programs/${programId}/${action}`, message)
      onSuccess()
    } catch (e) {
      console.error(`[recommendationStore] ${action} ${programId}`, e)
    }
  }

  async function expandProgram(
    program: ProgramRecommendationObj,
    expanded: boolean,
    source: DetailTabKey,
  ): Promise<void> {
    if (!expanded) return
    await postProgramAction(program.programId, 'expand', '', () => {
      track(TRACKING_EVENTS.PROGRAM_EXPANDED, toProgramInteractionPayload(program, source))
    })
  }

  async function askQuestionProgram(
    program: ProgramRecommendationObj,
    message: string,
    source: DetailTabKey,
  ): Promise<void> {
    await postProgramAction(program.programId, 'askquestion', message, () => {
      track(TRACKING_EVENTS.PROGRAM_QUESTION_ASKED, toProgramInteractionPayload(program, source))
    })
  }

  async function contactProgram(
    program: ProgramRecommendationObj,
    message: string,
    source: DetailTabKey,
  ): Promise<void> {
    await postProgramAction(program.programId, 'contact', message, () => {
      track(TRACKING_EVENTS.PROGRAM_CONTACTED, toProgramInteractionPayload(program, source))
    })
  }

  async function consultProgram(
    program: ProgramRecommendationObj,
    source: DetailTabKey,
  ): Promise<void> {
    await postProgramAction(program.programId, 'consult', '', () => {
      track(TRACKING_EVENTS.PROGRAM_CONSULTED, toProgramInteractionPayload(program, source))
    })
  }

  /**
   * Retire le programme des recommandations sans attendre le back, et le remet si le rejet échoue.
   * @param {ProgramRecommendationObj} program - Programme rejeté.
   * @param {DetailTabKey} source - Onglet d'origine du clic.
   * @returns {Promise<void>}
   */
  async function rejectProgram(
    program: ProgramRecommendationObj,
    source: DetailTabKey,
  ): Promise<void> {
    const previousPrograms: ProgramRecommendationObj[] = programs.value
    programs.value = previousPrograms.filter(
      (recommendedProgram: ProgramRecommendationObj): boolean =>
        recommendedProgram.programId !== program.programId,
    )

    try {
      await useFrontApi().post(
        `/api/Recommendations/programs/${program.programId}/actions/Rejection`,
        JSON.stringify(''),
        { headers: { 'Content-Type': 'application/json' } },
      )
      track(TRACKING_EVENTS.PROGRAM_REJECTED, toProgramInteractionPayload(program, source))
    } catch (e: unknown) {
      programs.value = previousPrograms
      console.error('[recommendationStore] rejectProgram', e)
    }
  }

  async function comparePrograms(programsToCompare: ProgramRecommendationObj[]): Promise<void> {
    try {
      // @Todo le back attend un `ProgramInputDto[]` (≠ recommandation) — shape du body à adapter
      await useFrontApi().post('/api/Recommendations/programs/compare', programsToCompare)
      track(TRACKING_EVENTS.PROGRAMS_COMPARED, { count: programsToCompare.length })
    } catch (e) {
      console.error('[recommendationStore] comparePrograms', e)
    }
  }

  async function checkAdmission(programsToCheck: ProgramRecommendationObj[]): Promise<void> {
    try {
      // @Todo le back attend un `ProgramInputDto[]` (≠ recommandation) — shape du body à adapter
      await useFrontApi().post('/api/Recommendations/programs/checkadmission', programsToCheck)
      track(TRACKING_EVENTS.ADMISSION_CHECKED, { count: programsToCheck.length })
    } catch (e) {
      console.error('[recommendationStore] checkAdmission', e)
    }
  }

  function reset(): void {
    controller?.abort()
    id.value = ''
    programs.value = []
    professions.value = []
    jobs.value = []
    status.value = 'idle'
  }

  return {
    id,
    programs,
    professions,
    jobs,
    status,
    programsCount,
    professionsCount,
    jobsCount,
    hasRecommendations,
    fetch,
    reset,
    expandProgram,
    askQuestionProgram,
    contactProgram,
    consultProgram,
    rejectProgram,
    comparePrograms,
    checkAdmission,
  }
})
